import type { RankingRewardSlice } from "@/types/RankingRewardSlice";
import { formatNumberWithCommas, pointsToYen } from "@/utils";
import { LaurelWreath, ThumbaIcon } from "@roman/shared/components/Icons";
import { cn } from "@roman/shared/utils";

interface RankingRewardsRowProps {
  rankingRewardSlice: RankingRewardSlice;
}

const PRIZE_CARD_COLORS: Record<string, string> = {
  "1": "flex items-center justify-center bg-gradient-to-br from-[#B88100] to-[#FFD000] w-[60px] rounded-ee-lg rounded-ss-lg bg-card shadow-sm bg-gradient-to-br",
  "2": "flex items-center justify-center bg-gradient-to-br from-[#7B8386] to-[#D9E5EB] w-[60px] rounded-ee-lg rounded-ss-lg bg-card shadow-sm bg-gradient-to-br",
  "3": "flex items-center justify-center bg-gradient-to-br from-[#AF6D21] to-[#F8CCAC] w-[60px] rounded-ee-lg rounded-ss-lg bg-card shadow-sm bg-gradient-to-br",
};

const COLOR_STYLES: Record<string, string> = {
  "1": "bg-gradient-to-b from-[#FFE500] to-[#3D4700] bg-clip-text",
  "2": "bg-gradient-to-b from-[#FFFFFF] to-[#414141] bg-clip-text",
  "3": "bg-gradient-to-b from-[#CBA080] to-[#693F0D] bg-clip-text",
};

const RANK_STYLES: Record<string, string> = {
  "1": "text-white text-center font-roboto font-black text-[30px] leading-[30px]",
  "2": "text-white text-center font-roboto font-black text-[30px] leading-[30px]",
  "3": "text-white text-center font-roboto font-black text-[30px] leading-[30px]",
};

const RANK_TEXT_STYLES: Record<string, string> = {
  "1": "text-white font-noto-sans font-medium text-[12px] leading-[17.44px]",
  "2": "text-white font-noto-sans font-medium text-[12px] leading-[17.44px]",
  "3": "text-white font-noto-sans font-medium text-[12px] leading-[17.44px]",
};

const BACKGROUND_COLORS: Record<string, string> = {
  "1": "bg-[#FFF5CC] rounded-ss-lg",
  "2": "bg-[#ECF0F0] rounded-ss-lg",
  "3": "bg-[#FAEEE6] rounded-ss-lg",
};

const getPrizeCardColorByRankRange = (rank: string) =>
  PRIZE_CARD_COLORS[rank] || "pl-[14px] bg-white w-full flex items-start justify-center flex-col";

const getColorStyleByRankRange = (rank: string) => COLOR_STYLES[rank] || "";

const getRankStyle = (rank: string) =>
  RANK_STYLES[rank] || "text-black text-center font-roboto font-bold text-[24px] leading-[24px]";

const getRankTextStyle = (rank: string) =>
  RANK_TEXT_STYLES[rank] || "text-dark-gray text-center font-noto-sans font-medium text-[12px] leading-[12px]";

const renderPercentage = (rankingRewardSlice: RankingRewardSlice) =>
  rankingRewardSlice.percentage ? (
    <div className="text-dark-gray font-inter text-[10px] font-normal leading-none">
      上位 <span className="font-semibold">{rankingRewardSlice.percentage}</span>%
    </div>
  ) : null;

export const getBackgroundColorByRank = (rank: string) =>
  BACKGROUND_COLORS[rank] || "bg-white border-b-[0.5px] border-dashed border-light-gray";

export const RankingRewardsRow = ({ rankingRewardSlice }: RankingRewardsRowProps) => {
  return (
    <div className={` ${getBackgroundColorByRank(rankingRewardSlice.rank)} flex items-center gap-[6px]`}>
      <div className="w-1/3">
        <div className={cn("w-full min-h-[50px]", getColorStyleByRankRange(rankingRewardSlice.rank))}>
          <div
            className={cn(
              "h-full min-h-[50px] relative overflow-hidden",
              getPrizeCardColorByRankRange(rankingRewardSlice.rank),
            )}
          >
            <ThumbaIcon className="absolute top-[-20px] left-[-10px] text-white/10" />

            <div className="flex items-center justify-center relative">
              {rankingRewardSlice.rank === "1" && (
                <LaurelWreath color="#FAE08B" className="absolute w-[60px] h-[50px]" />
              )}
              <div>
                <span className={`${getRankStyle(rankingRewardSlice.rank)}`}>{rankingRewardSlice.rank}</span>
                <span className={`${getRankTextStyle(rankingRewardSlice.rank)}`}>位</span>
              </div>
            </div>
            {renderPercentage(rankingRewardSlice)}
          </div>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-black text-center font-roboto font-bold text-[18px] leading-[26.15px]">
          {formatNumberWithCommas((rankingRewardSlice.minReachablePoints ?? 0).toString())}
          <span className="text-[12px] font-normal">pt</span>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-black text-center font-roboto font-bold text-[18px] leading-[26.15px]">
          {pointsToYen(rankingRewardSlice.amount)}
          <span className="text-[12px] font-normal">円</span>
        </div>
      </div>
    </div>
  );
};
